/**
 * CS, Services and Finance tab tiles (quick-261104-ia3, John 2026-11-04: "a tab
 * for cs, a tab for services, and a tab for finance").
 *
 * Rillet figures come from the dated constant (lib/exec/rillet-arr.ts); the
 * Salesforce renewal + managed-services figures from GET /api/main/exec.
 */
import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import { BAR_RADIUS, INK, SERIES, baseOption, stageColor, tipHead, tipNote, tipRow } from '../../lib/exec/chart.js';
import { dayLabel, money, monthLabel, num, pct } from '../../lib/exec/fmt.js';
import { sfLink } from '../../lib/exec/links.js';
import { todayIso } from '../../lib/exec/periods.js';
import { useRillet } from '../../lib/exec/rillet-source.js';

import { DashTile, Legend, TileEmpty } from './DashTile.js';

import type { ExecCsPanel, ExecRilletPanel, ExecServicesPanel } from '@saledash/shared';
import type { EChartsOption } from 'echarts';

function rilletFoot(R: ExecRilletPanel): string {
  return `Rillet · pulled ${R.pulledOn} · ${monthLabel(R.provisionalMonth)} provisional`;
}

function trailing12(R: ExecRilletPanel) {
  const months = R.months.slice(-12);
  const start = R.months[R.months.length - 13]?.arr ?? 0;
  const sum = (k: 'newArr' | 'expansionArr' | 'contractionArr' | 'churnArr'): number => months.reduce((s, m) => s + m[k], 0);
  return { months, start, newArr: sum('newArr'), exp: sum('expansionArr'), contr: sum('contractionArr'), churn: sum('churnArr') };
}

/** Diverging monthly bars: added (up) vs lost (down). */
function gainLossOption(withNew: boolean, R: ExecRilletPanel): EChartsOption {
  const { months } = trailing12(R);
  const base = baseOption({ yMoney: true, left: 48 });
  return {
    ...base,
    xAxis: { ...(base.xAxis as object), data: months.map((m) => monthLabel(m.month)) },
    tooltip: {
      ...(base.tooltip as object),
      formatter: (params: unknown) => {
        const m = months[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
        if (!m) return '';
        return (
          tipHead(monthLabel(m.month)) +
          (withNew ? tipRow(SERIES.aqua, 'New customers', money(m.newArr)) : '') +
          tipRow(SERIES.blue, 'Existing customers grew', money(m.expansionArr)) +
          tipRow('#f2a3a2', 'Existing customers shrank', money(m.contractionArr)) +
          tipRow(SERIES.red, 'Customers who left', money(m.churnArr)) +
          (m.month === R.provisionalMonth ? tipNote('Provisional month: renewals not yet booked read as lost.') : tipNote('Above the line = recurring revenue added; below = lost.'))
        );
      },
    },
    series: [
      ...(withNew
        ? [{ name: 'New', type: 'bar' as const, stack: 'up', barMaxWidth: 22, data: months.map((m) => m.newArr), itemStyle: { color: SERIES.aqua, borderColor: '#fff', borderWidth: 1 } }]
        : []),
      { name: 'Grew', type: 'bar' as const, stack: 'up', barMaxWidth: 22, data: months.map((m) => m.expansionArr), itemStyle: { color: SERIES.blue, borderRadius: BAR_RADIUS, borderColor: '#fff', borderWidth: 1 } },
      { name: 'Shrank', type: 'bar' as const, stack: 'down', barMaxWidth: 22, data: months.map((m) => -m.contractionArr), itemStyle: { color: '#f2a3a2', borderColor: '#fff', borderWidth: 1 } },
      { name: 'Left', type: 'bar' as const, stack: 'down', barMaxWidth: 22, data: months.map((m) => -m.churnArr), itemStyle: { color: SERIES.red, borderRadius: [0, 0, 4, 4], borderColor: '#fff', borderWidth: 1 } },
    ],
  };
}

// ═══ CS ══════════════════════════════════════════════════════════════════════
export function RetentionTile(): React.JSX.Element {
  const R = useRillet();
  const t = trailing12(R);
  const grr = t.start ? (t.start - t.churn - t.contr) / t.start : null;
  const nrr = t.start ? (t.start - t.churn - t.contr + t.exp) / t.start : null;
  const option = useMemo(() => gainLossOption(false, R), [R]);
  return (
    <DashTile
      title="Customer retention"
      info="How much recurring revenue we kept from existing customers over the last 12 months. Net retention counts growth from existing customers; gross retention only counts what we kept. Above 100% net means existing customers grew faster than we lost them."
      headline={`${pct(nrr)} net`}
      sub={`${pct(grr)} gross retention · last 12 months`}
      size="lg"
      footer={rilletFoot(R)}
      testId="cs-retention"
    >
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1">
          <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
        </div>
        <Legend items={[{ label: 'Grew', color: SERIES.blue }, { label: 'Shrank', color: '#f2a3a2' }, { label: 'Left', color: SERIES.red }]} />
      </div>
    </DashTile>
  );
}

export function UpcomingRenewalsTile(): React.JSX.Element {
  const R = useRillet();
  const today = todayIso();
  const rows = R.renewals.filter((r) => r.endDate >= today);
  const open = rows.filter((r) => !r.renewed);
  return (
    <DashTile
      title="Contracts up for renewal"
      info="Customer contracts in Rillet that end in the next six months, soonest first. A ✓ means a renewal contract is already booked; the rest still need one. Values are total contract value."
      headline={money(open.reduce((s, r) => s + r.value, 0))}
      sub={`${num(open.length)} of ${num(rows.length)} contracts not yet renewed`}
      size="xl"
      footer={`Rillet in-effect contracts ending within 180 days · pulled ${R.pulledOn}`}
      testId="cs-renewals"
    >
      {rows.length === 0 ? (
        <TileEmpty text="No contracts end in the next six months." />
      ) : (
        <div className="h-full overflow-y-auto pr-1">
          <table className="w-full text-left text-xs">
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.customer}-${r.contract}-${r.endDate}`} className="border-t border-ink-100 first:border-0">
                  <td className="w-5 py-1.5">
                    {r.renewed ? (
                      <span className="text-good-text" title="Renewal booked">
                        ✓
                      </span>
                    ) : (
                      <span className="text-ink-300" title="No renewal booked yet">
                        ○
                      </span>
                    )}
                  </td>
                  <td className="max-w-[12rem] truncate py-1.5 pr-2 text-ink-900" title={`${r.customer} · ${r.contract}`}>
                    {r.customer}
                  </td>
                  <td className="py-1.5 pr-2 tabular-nums text-ink-500">{dayLabel(r.endDate)}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink-900">{money(r.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashTile>
  );
}

export function ChurnedCustomersTile(): React.JSX.Element {
  const R = useRillet();
  const rows = R.months.slice(-12)
    .flatMap((m) => m.churned.map((c) => ({ ...c, month: m.month })))
    .sort((a, b) => b.month.localeCompare(a.month) || b.arr - a.arr);
  return (
    <DashTile
      title="Customers we lost"
      info="Every customer whose recurring revenue went to zero in the last 12 months, newest first, with the ARR they took with them. The latest month may include renewals not yet booked."
      headline={money(rows.reduce((s, r) => s + r.arr, 0))}
      sub={`${num(rows.length)} customers · last 12 months`}
      size="xl"
      footer={rilletFoot(R)}
      testId="cs-churned"
    >
      <div className="h-full overflow-y-auto pr-1">
        <table className="w-full text-left text-xs">
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.month}-${r.name}`} className="border-t border-ink-100 first:border-0">
                <td className="max-w-[12rem] truncate py-1.5 pr-2 text-ink-900">{r.name}</td>
                <td className="py-1.5 pr-2 text-ink-500">
                  {monthLabel(r.month)}
                  {r.month === R.provisionalMonth ? <span className="ml-1 text-[0.6rem] text-warn-text">provisional</span> : null}
                </td>
                <td className="py-1.5 text-right tabular-nums text-ink-900">{money(r.arr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashTile>
  );
}

export function ConcentrationTile(): React.JSX.Element {
  const R = useRillet();
  const last = R.months[R.months.length - 1];
  const total = last?.arr ?? 0;
  const top = R.topCustomers.slice(0, 10);
  const top10 = top.reduce((s, c) => s + c.arr, 0);
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true });
    return {
      ...base,
      grid: { left: 140, right: 56, top: 4, bottom: 4 },
      xAxis: { type: 'value', show: false },
      yAxis: { type: 'category', inverse: true, data: top.map((c) => c.name), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: INK.secondary, fontSize: 11, width: 130, overflow: 'truncate' } },
      tooltip: {
        ...(base.tooltip as object),
        formatter: (params: unknown) => {
          const c = top[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          return c ? tipHead(`${c.name} · ${money(c.arr)}`) + tipRow(null, 'Share of all ARR', pct(total ? c.arr / total : null)) + tipNote('How much of our recurring revenue depends on this one customer.') : '';
        },
      },
      series: [{ type: 'bar', barWidth: '62%', data: top.map((c) => c.arr), itemStyle: { color: SERIES.blue, borderRadius: [0, 4, 4, 0] }, label: { show: true, position: 'right', color: INK.secondary, fontSize: 10, formatter: (p: unknown) => pct(total ? (p as { value: number }).value / total : null) } }],
    };
  }, [top, total]);
  return (
    <DashTile
      title="Largest customers"
      info="Our ten biggest customers by recurring revenue and the share of total ARR each one represents. A high share means losing one customer would hurt a lot."
      headline={pct(total ? top10 / total : null)}
      sub={`of ARR in the top 10 · ${num(R.customerCount)} paying customers`}
      size="xl"
      footer={rilletFoot(R)}
      testId="cs-concentration"
    >
      <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
    </DashTile>
  );
}

export function SfRenewalsTile({ cs }: { cs: ExecCsPanel | undefined }): React.JSX.Element {
  const today = todayIso();
  const pastDue = (cs?.upcoming ?? []).filter((r) => r.date < today);
  const q = cs?.closedByQuarter ?? [];
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true });
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: q.map((x) => x.label) },
      tooltip: {
        ...(base.tooltip as object),
        formatter: (params: unknown) => {
          const x = q[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          return x ? tipHead(x.label) + tipRow(SERIES.blue, `Renewed (${String(x.wonN)})`, money(x.won)) + tipRow(SERIES.red, `Not renewed (${String(x.lostN)})`, money(x.lost)) + tipNote('Renewal opportunities closed in Salesforce that quarter.') : '';
        },
      },
      series: [
        { name: 'Renewed', type: 'bar', barMaxWidth: 22, data: q.map((x) => x.won), itemStyle: { color: SERIES.blue, borderRadius: BAR_RADIUS } },
        { name: 'Not renewed', type: 'bar', barMaxWidth: 22, data: q.map((x) => x.lost), itemStyle: { color: SERIES.red, borderRadius: BAR_RADIUS } },
      ],
    };
  }, [q]);
  return (
    <DashTile
      title="Renewals in Salesforce"
      info="Renewal opportunities closed in Salesforce each quarter, renewed versus not. The count below shows open renewal records already past their close date: until those are closed out, Salesforce under-reports renewals, so Rillet is the source of truth for retention."
      headline={num(pastDue.length)}
      sub="open renewal records past their date"
      size="lg"
      footer="Salesforce Renewals record type"
      testId="cs-sf-renewals"
    >
      {!cs ? (
        <TileEmpty text="Loading…" />
      ) : (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          <Legend items={[{ label: 'Renewed', color: SERIES.blue }, { label: 'Not renewed', color: SERIES.red }]} />
        </div>
      )}
    </DashTile>
  );
}

// ═══ Services ════════════════════════════════════════════════════════════════
export function ServicesWonTile({ services }: { services: ExecServicesPanel | undefined }): React.JSX.Element {
  const q = (services?.wonByQuarter ?? []).slice(-6);
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true });
    return {
      ...base,
      xAxis: { ...(base.xAxis as object), data: q.map((x) => x.label) },
      tooltip: {
        ...(base.tooltip as object),
        formatter: (params: unknown) => {
          const x = q[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          return x ? tipHead(`${x.label} · ${money(x.amount)}`) + tipRow(null, 'Deals won', num(x.count)) + tipNote('Managed services contracts won in the quarter.') : '';
        },
      },
      series: [{ type: 'bar', barMaxWidth: 30, data: q.map((x) => x.amount), itemStyle: { color: SERIES.blue, borderRadius: BAR_RADIUS } }],
    };
  }, [q]);
  return (
    <DashTile
      title="Managed services won"
      info="Dollars of managed services deals won each quarter, from Salesforce's Managed Services opportunities."
      headline={money(q.reduce((s, x) => s + x.amount, 0))}
      sub={`${num(q.reduce((s, x) => s + x.count, 0))} deals · last ${String(q.length)} quarters`}
      size="lg"
      footer="Salesforce Managed Services record type"
      testId="svc-won"
    >
      {!services ? <TileEmpty text="Loading…" /> : q.length === 0 ? <TileEmpty text="No managed services wins in the window." /> : <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />}
    </DashTile>
  );
}

export function ServicesPipelineTile({ services }: { services: ExecServicesPanel | undefined }): React.JSX.Element {
  const deals = services?.openDeals ?? [];
  return (
    <DashTile
      title="Managed services pipeline"
      info="Open managed services deals, soonest close first, with their stage and size."
      headline={money(deals.reduce((s, d) => s + d.amount, 0))}
      sub={`${num(deals.length)} open deals`}
      size="lg"
      footer="Click a deal to open it in Salesforce"
      testId="svc-pipeline"
    >
      {!services ? (
        <TileEmpty text="Loading…" />
      ) : deals.length === 0 ? (
        <TileEmpty text="No open managed services deals." />
      ) : (
        <div className="h-full overflow-y-auto pr-1">
          <table className="w-full text-left text-xs">
            <tbody>
              {deals.map((d) => (
                <tr key={d.id} className="border-t border-ink-100 first:border-0">
                  <td className="max-w-[12rem] truncate py-1.5 pr-2">
                    <a href={sfLink(d.id)} className="text-brand-600 hover:underline" title={d.name}>
                      {d.account ?? d.name}
                    </a>
                  </td>
                  <td className="py-1.5 pr-2">
                    <span className="inline-flex items-center gap-1 text-ink-600">
                      <span aria-hidden className="inline-block h-2 w-2 rounded-[2px]" style={{ background: stageColor(d.stage) }} />
                      {d.stage}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 tabular-nums text-ink-500">{dayLabel(d.closeDate)}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink-900">{money(d.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashTile>
  );
}

export function ServicesForecastLinkTile(): React.JSX.Element {
  return (
    <DashTile
      title="Services forecast"
      info="Time-and-materials services and the managed services forecast against budget live on the Forecast page, which reads Finance's Services Deal Outlook upload."
      size="lg"
      footer="Forecast page · Finance upload"
      testId="svc-forecast"
    >
      <div className="flex h-full flex-col items-start justify-center gap-3 rounded-lg bg-ink-50 p-4">
        <p className="text-sm text-ink-700">Services revenue against budget, by quarter, is on the Forecast page.</p>
        <Link to="/forecast" className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700">
          Open the forecast ↗
        </Link>
      </div>
    </DashTile>
  );
}

// ═══ Finance ═════════════════════════════════════════════════════════════════
export function ArrMovementTile(): React.JSX.Element {
  const R = useRillet();
  const t = trailing12(R);
  const option = useMemo(() => gainLossOption(true, R), [R]);
  return (
    <DashTile
      title="ARR movement by month"
      info="What changed recurring revenue each month: new customers and growth from existing customers above the line, shrinkage and lost customers below it."
      headline={money(t.newArr + t.exp - t.contr - t.churn)}
      sub="net ARR change, last 12 months"
      size="lg"
      footer={rilletFoot(R)}
      testId="fin-arr-movement"
    >
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1">
          <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
        </div>
        <Legend items={[{ label: 'New', color: SERIES.aqua }, { label: 'Grew', color: SERIES.blue }, { label: 'Shrank', color: '#f2a3a2' }, { label: 'Left', color: SERIES.red }]} />
      </div>
    </DashTile>
  );
}

export function ArrBridgeTile(): React.JSX.Element {
  const R = useRillet();
  const t = trailing12(R);
  const end = R.months[R.months.length - 1]?.arr ?? 0;
  const steps = [
    { label: `Start (${monthLabel(R.months[R.months.length - 13]?.month ?? '')})`, v: t.start, kind: 'total' as const },
    { label: 'New customers', v: t.newArr, kind: 'up' as const },
    { label: 'Existing grew', v: t.exp, kind: 'up' as const },
    { label: 'Existing shrank', v: -t.contr, kind: 'down' as const },
    { label: 'Customers left', v: -t.churn, kind: 'down' as const },
    { label: `Now (${monthLabel(R.provisionalMonth)})`, v: end, kind: 'total' as const },
  ];
  const option = useMemo<EChartsOption>(() => {
    const base = baseOption({ yMoney: true });
    let running = 0;
    const invisible: number[] = [];
    const visible: { value: number; itemStyle: { color: string; borderRadius: [number, number, number, number] } }[] = [];
    for (const s of steps) {
      if (s.kind === 'total') {
        invisible.push(0);
        visible.push({ value: s.v, itemStyle: { color: INK.secondary, borderRadius: [0, 4, 4, 0] } });
        running = s.v;
      } else if (s.kind === 'up') {
        invisible.push(running);
        visible.push({ value: s.v, itemStyle: { color: SERIES.blue, borderRadius: [0, 4, 4, 0] } });
        running += s.v;
      } else {
        running += s.v;
        invisible.push(running);
        visible.push({ value: -s.v, itemStyle: { color: SERIES.red, borderRadius: [0, 4, 4, 0] } });
      }
    }
    return {
      ...base,
      grid: { left: 110, right: 56, top: 4, bottom: 4 },
      xAxis: { type: 'value', show: false, min: Math.floor((Math.min(t.start, end) * 0.85) / 1_000_000) * 1_000_000 },
      yAxis: { type: 'category', inverse: true, data: steps.map((s) => s.label), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: INK.secondary, fontSize: 11 } },
      tooltip: {
        ...(base.tooltip as object),
        formatter: (params: unknown) => {
          const s = steps[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
          return s ? tipHead(`${s.label} · ${s.kind === 'total' ? money(s.v) : `${s.v >= 0 ? '+' : '−'}${money(Math.abs(s.v))}`}`) + tipNote('Walks ARR from a year ago to today: what we added and what we lost.') : '';
        },
      },
      series: [
        { type: 'bar', stack: 'b', itemStyle: { color: 'transparent' }, emphasis: { disabled: true }, data: invisible, tooltip: { show: false } },
        {
          type: 'bar',
          stack: 'b',
          barWidth: '58%',
          data: visible,
          label: { show: true, position: 'right', color: INK.secondary, fontSize: 10, formatter: (p: unknown) => money((p as { value: number }).value) },
        },
      ],
    };
  }, [steps, t.start, end]);
  return (
    <DashTile
      title="ARR bridge, last 12 months"
      info="How ARR got from a year ago to today: the starting figure, plus new customers and growth, minus shrinkage and lost customers, equals ARR now."
      headline={money(end)}
      sub={`from ${money(t.start)} a year ago`}
      size="lg"
      footer={rilletFoot(R)}
      testId="fin-arr-bridge"
    >
      <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
    </DashTile>
  );
}

export function FinanceLinksTile(): React.JSX.Element {
  return (
    <DashTile title="Forecast and plan" info="The revenue forecast against budget and the 2027 plan model live on their own pages." size="lg" footer="Same sign-in" testId="fin-links">
      <div className="flex h-full flex-col justify-center gap-3 rounded-lg bg-ink-50 p-4">
        <Link to="/forecast" className="rounded-md border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-900 hover:border-brand-300">
          Combined revenue forecast vs budget ↗
        </Link>
        <Link to="/2027-plan" className="rounded-md border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-900 hover:border-brand-300">
          2027 plan model ↗
        </Link>
      </div>
    </DashTile>
  );
}
